import React, { memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectCollapseAll,
  selectUnNestAll,
  selectUnnestParentIds
} from 'app/pages/Home/slice/selectors';
import { estimateMonthlyPlacementBlockWidth } from '../utils/estimateMonthlyInlineItemWidth';
import { selectItemExpandVersion } from '../store/selectors';
import {
  computeColumnAwareRenderTops,
  MIN_ROW_HEIGHT_PX,
  MONTHLY_ROW_LAYER_PADDING_BOTTOM_PX
} from '../utils/placeItemsInTracks';
import {
  buildMonthlyTrackedPositions,
  type MonthlyTrackedPositionBase
} from '../utils/buildMonthlyTrackedPositions';
import {
  getMonthlyItemBlockHeightPx,
  getMonthlyMustWinInstanceKey,
  resolveMonthlyItemExpanded
} from '../utils/monthlyExpandUtils';
import { MonthlyItemTree } from './MonthlyItemTree';
import { MonthlyKeyDateRow } from './MonthlyKeyDateRow';
import type { MonthDescriptor, MonthlyTimelineLane } from '../utils/types';
import styles from '../MonthlyRoadmap.module.scss';

interface MonthlyRowProps {
  lane: MonthlyTimelineLane;
  months: MonthDescriptor[];
  getDayLeftPx: (monthKey: string, day: number) => number | null;
  bodyWidthPx: number;
  mustWinRowKey?: string;
}

type RowTrackedPosition = MonthlyTrackedPositionBase & { heightPx: number };

type PositionedItem = {
  key: string;
  leftPx: number;
  renderTopPx: number;
  widthPx: number;
  heightPx: number;
  rowIndex: number;
  mustWinInstanceKey?: string;
  item: RowTrackedPosition['item'];
};

export const MonthlyRow = memo(
  ({
    lane,
    months,
    getDayLeftPx,
    bodyWidthPx,
    mustWinRowKey
  }: MonthlyRowProps) => {
    const itemExpandVersion = useSelector(selectItemExpandVersion);
    const isCollapsed = useSelector(selectCollapseAll);
    const isUnNested = useSelector(selectUnNestAll);
    const unnestParentIds = useSelector(selectUnnestParentIds);

    const positionedItems = useMemo<PositionedItem[]>(() => {
      const expandContext = {
        isCollapsed,
        isUnNested,
        unnestParentIds
      };

      const placed = buildMonthlyTrackedPositions<RowTrackedPosition>({
        items: lane.items,
        months,
        getDayLeftPx,
        resolvePlacement: item => {
          const mustWinInstanceKey = mustWinRowKey
            ? getMonthlyMustWinInstanceKey(mustWinRowKey, item)
            : undefined;
          const isExpanded = resolveMonthlyItemExpanded(item, {
            ...expandContext,
            mustWinInstanceKey
          });

          return {
            widthPx: estimateMonthlyPlacementBlockWidth(
              item,
              lane.label,
              isExpanded
            ),
            heightPx: getMonthlyItemBlockHeightPx(item, isExpanded)
          };
        }
      }).map((entry, index) => {
        const item = entry.item;
        const mustWinInstanceKey = mustWinRowKey
          ? getMonthlyMustWinInstanceKey(mustWinRowKey, item)
          : undefined;

        return {
          key: `${item?.issueId || item?.id || 'item'}-${index}`,
          leftPx: entry.leftPx,
          widthPx: entry.widthPx,
          heightPx: entry.heightPx,
          rowIndex: entry.rowIndex,
          mustWinInstanceKey,
          item
        };
      });

      const sortedForTops = [...placed].sort((a, b) => {
        if (a.rowIndex !== b.rowIndex) {
          return a.rowIndex - b.rowIndex;
        }

        return a.leftPx - b.leftPx;
      });

      return computeColumnAwareRenderTops(sortedForTops);
    }, [
      lane.items,
      lane.label,
      months,
      getDayLeftPx,
      isCollapsed,
      isUnNested,
      unnestParentIds,
      itemExpandVersion,
      mustWinRowKey
    ]);

    const rowHeightPx = useMemo(() => {
      if (!positionedItems.length) return MIN_ROW_HEIGHT_PX;

      const maxContentBottom = Math.max(
        ...positionedItems.map(item => item.renderTopPx + item.heightPx)
      );

      return Math.max(
        MIN_ROW_HEIGHT_PX,
        maxContentBottom + MONTHLY_ROW_LAYER_PADDING_BOTTOM_PX
      );
    }, [positionedItems]);

    return (
      <div
        className={styles.monthlyRowItemLayer}
        style={{
          minHeight: `${rowHeightPx}px`,
          width: `${bodyWidthPx}px`
        }}
      >
        {positionedItems.map(positionedItem => (
          <div
            key={positionedItem.key}
            className={styles.monthlyRowItemSlot}
            style={{
              left: `${positionedItem.leftPx}px`,
              top: `${positionedItem.renderTopPx}px`
            }}
          >
            {lane.isKeyDate ? (
              <MonthlyKeyDateRow item={positionedItem.item} />
            ) : (
              <MonthlyItemTree
                item={positionedItem.item}
                laneLabel={lane.label}
                mustWinInstanceKey={positionedItem.mustWinInstanceKey}
              />
            )}
          </div>
        ))}
      </div>
    );
  }
);
